import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"

import { cn } from "@/lib/utils"

// Plain shadcn badge: a full pill, 22px tall, 12px text. `secondary` (grey fill) and
// `outline` (white + hairline) are the two the console leans on; a leading dot carries the
// tone so a queue of states reads as one calm column rather than a row of coloured blocks.
// `success` /
// `warning` / `neutral` are ported verbatim from the bespoke tones map (same flat 12%-tint
// formula); `destructive` is the prebuilt's own — it already matched that exact doctrine.
//
// `info`: the bespoke version tinted this blue (`--link`), but blue is reserved for the
// accent/selection color house-wide, so an info-tone badge can't reuse it. Recolored to a
// flat ink outline (no tint, no fill) instead of picking a new hue — keeps it visually
// distinct from `neutral` (filled) without introducing a color the palette doesn't own.
const badgeVariants = cva(
  "group/badge inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pe-2 has-data-[icon=inline-start]:ps-2 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20",
        outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        neutral: "bg-secondary text-secondary-foreground",
        success: "bg-[var(--success)]/12 text-[var(--success)]",
        warning: "bg-[var(--warning)]/12 text-[var(--warning)]",
        info: "border-border text-foreground",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
)

function Badge({
  className,
  variant = "neutral",
  render,
  tone,
  ...props
}: useRender.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    tone?: "neutral" | "outline" | "primary" | "success" | "warning" | "danger"
  }) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(
          badgeVariants({
            variant: tone === "primary" ? "default" : tone === "danger" ? "destructive" : (tone ?? variant),
          }),
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

/**
 * The 6px tone dot a state badge leads with. Kept a separate export (rather than a `tone`
 * prop on Badge) because the dot's colour is a DATA question — the cash state vocabulary owns
 * the mapping — while the badge's fill is a chrome question.
 */
function BadgeDot({ className, ...props }: React.ComponentProps<"span">) {
  return <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", className)} {...props} />
}

export { Badge, BadgeDot, badgeVariants }
